/**
 * Starting a camera without leaving one running.
 *
 * Extracted from QrScanner for the same reason `realtimeTransport.ts` was
 * extracted from `realtime.ts`: the interesting part is a race, and a race is
 * worth testing without a DOM.
 *
 * ## The race
 *
 * `decodeFromVideoDevice` is asynchronous and frequently *slow* — it enumerates
 * devices and calls `getUserMedia`, which on a phone means waiting for the user
 * to answer a permission prompt. React's cleanup, by contrast, runs the instant
 * the component unmounts.
 *
 * So the ordinary case on a shop floor is:
 *
 *   1. worker opens the scanner, the browser asks for camera permission
 *   2. worker taps back, or the sheet closes — the effect cleans up
 *   3. *then* the promise resolves and hands over a live camera stream
 *
 * At step 2 there is nothing to stop yet, and at step 3 nobody is listening.
 * The stream stays open: on a phone that is the camera indicator staying lit and
 * the battery draining, with the app apparently closed. Which is the exact
 * failure QrScanner's own docblock says it exists to prevent — it guarded the
 * result callback with a flag, but never the stream.
 */
export interface ScannerControls {
  stop: () => void
}

/**
 * Runs `start`, and stops whatever it produced if the caller gave up waiting.
 *
 * Returns null when cancelled, so the caller can tell "started" from "started
 * and immediately thrown away" without inspecting anything.
 */
export async function startCancellableScan(
  start: () => Promise<ScannerControls>,
  isCancelled: () => boolean,
): Promise<ScannerControls | null> {
  const controls = await start()

  if (isCancelled()) {
    // The only line that matters. Everything above is ceremony.
    controls.stop()

    return null
  }

  return controls
}
